import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, CustomSelect, Label, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { useProjectStoreBase, updateProjectInLists } from '../stores/pmProjectStore';
import { createProject, updateProject, getCrmQuotes, CrmQuote } from '../services/api';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { notifyDataChanged } from '../utils/dataEvents';
import { PMProject } from '../types/pm';
import { getProjectTypeOptions } from '../utils/constants';
import { useCrmCustomers, withCurrentValue } from '../stores/lookupStore';
import { useUnsavedChangesWarning } from '@redpanda/utils';

interface ProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project?: PMProject; // 型別與 pmProjectStore 同一份定義
  /** 新增時預先選好的 CRM 報價（從 CRM 報價列表「建立 PM 專案」進來） */
  initialQuoteId?: string;
}

interface FormData {
  name: string;
  description?: string;
  projectType: string;
  customerId?: string;
  totalBudget?: number;
  crmQuoteId?: string;
  consultingStartDate?: string;
  consultingEndDate?: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
}

const ProjectDialog: React.FC<ProjectDialogProps> = ({
  isOpen,
  onClose,
  project,
  initialQuoteId,
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const { t } = useTranslation(['translation', 'pages']);

  const isEditMode = !!project;
  // 已分包的專案，總預算由各包金額自動加總，不可直接修改
  const hasPackages = isEditMode && ((project.packages?.length ?? project._count?.packages ?? 0) > 0);

  // 監聽專案類型
  const watchedProjectType = watch('projectType', 'WEBSITE');

  // 客戶：從 CRM 選（PM 後端代查，不需要 CRM 權限）；已封存的客戶只在已選時列出
  const watchedCustomerId = watch('customerId', '');
  const customers = useCrmCustomers();
  const customerOptions = withCurrentValue(
    [
      { value: '', label: t('pmProjects.noCustomer', { ns: 'pages' }) },
      ...customers
        .filter(c => !c.archived || c.id === watchedCustomerId)
        .map(c => ({ value: c.id, label: c.company ? `${c.name}（${c.company}）` : c.name })),
    ],
    watchedCustomerId
  );

  // 從已成交的 CRM 報價帶入（只在新增時）：客戶、金額以報價為準，報價項目在建立時轉成需求
  const [quotes, setQuotes] = useState<CrmQuote[]>([]);
  const watchedQuoteId = watch('crmQuoteId', '');
  const selectedQuote = quotes.find(q => q.id === watchedQuoteId);
  useEffect(() => {
    if (!isOpen || isEditMode) return;
    let cancelled = false;
    getCrmQuotes()
      .then(({ data }) => {
        if (cancelled) return;
        setQuotes(data);
        const initial = initialQuoteId && data.find(q => q.id === initialQuoteId && !q.projectId);
        if (initial) applyQuote(initial);
        else if (initialQuoteId) toast.error(t('pmProjects.fromQuote.unavailable', { ns: 'pages' }));
      })
      .catch(error => console.error('Failed to load CRM quotes:', error));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, initialQuoteId]);

  const formatAmount = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const quoteOptions = [
    { value: '', label: t('pmProjects.fromQuote.none', { ns: 'pages' }) },
    ...quotes
      .filter(q => !q.projectId)
      .map(q => ({ value: q.id, label: `${q.title}｜${q.customer?.company || q.customer?.name || ''}｜$${formatAmount(q.totalAmount)}` })),
  ];
  const applyQuote = (quote: CrmQuote | undefined) => {
    setValue('crmQuoteId', quote?.id ?? '', { shouldDirty: true });
    if (!quote) return;
    setValue('name', quote.title, { shouldDirty: true, shouldValidate: true });
    setValue('customerId', quote.customerId, { shouldDirty: true });
    setValue('totalBudget', quote.totalAmount, { shouldDirty: true });
  };

  // 關閉確認邏輯
  const actualHandleClose = () => {
    reset();
    onClose();
  };

  const {
    handleClose: handleCloseWithWarning,
    showConfirm,
    handleConfirmClose,
    handleCancelClose,
  } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      const today = getTodayDateInput();

      if (project) {
        // 編輯模式：使用 reset 設置初始值，避免標記為 dirty
        reset({
          name: project.name,
          description: project.description || '',
          projectType: project.projectType,
          customerId: project.customerId || '',
          totalBudget: project.totalBudget || undefined,
          consultingStartDate: project.consultingStartDate ? toDateInput(project.consultingStartDate) : '',
          consultingEndDate: project.consultingEndDate ? toDateInput(project.consultingEndDate) : '',
          expectedDeliveryDate: project.expectedDeliveryDate ? toDateInput(project.expectedDeliveryDate) : '',
          actualDeliveryDate: project.actualDeliveryDate ? toDateInput(project.actualDeliveryDate) : '',
        });
      } else {
        // 新增模式：使用 reset 設置預設值
        reset({
          name: '',
          description: '',
          projectType: 'WEBSITE',
          customerId: '',
          totalBudget: undefined,
          crmQuoteId: '',
          consultingStartDate: today,
          consultingEndDate: today,
          expectedDeliveryDate: '',
          actualDeliveryDate: '',
        });
      }
    }
  }, [isOpen, project, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: any = {
      name: data.name.trim(),
      description: data.description?.trim() || null,
      projectType: data.projectType,
      customerId: data.customerId?.trim() || null,
      ...(!hasPackages && { totalBudget: data.totalBudget ? Number(data.totalBudget) : null }),
      ...(!isEditMode && data.crmQuoteId && { crmQuoteId: data.crmQuoteId }),
      consultingStartDate: data.consultingStartDate ? toISOString(data.consultingStartDate) : null,
      consultingEndDate: data.consultingEndDate ? toISOString(data.consultingEndDate) : null,
      expectedDeliveryDate: data.expectedDeliveryDate ? toISOString(data.expectedDeliveryDate) : null,
      actualDeliveryDate: data.actualDeliveryDate ? toISOString(data.actualDeliveryDate) : null,
    };

    if (isEditMode) {
      const originalProject = { ...project };
      updateProjectInLists(project.id, payload);
      onClose();

      try {
        const response = await updateProject(project.id, payload);
        if (response?.data) updateProjectInLists(project.id, response.data);
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('projects');
      } catch (error: any) {
        updateProjectInLists(project.id, originalProject);
        toast.error(error.response?.data?.error || error.message || t('common.messages.operationFailed'));
      }
    } else {
      const tempId = `temp-${Date.now()}`;
      const optimisticProject = {
        id: tempId,
        organizationId: '',
        userId: '',
        ...payload,
        status: 'PLANNING',
        paidAmount: 0,
        pendingAmount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as any;
      useProjectStoreBase.getState().addItem(optimisticProject);
      onClose();

      try {
        const response = await createProject(payload);
        if (response?.data) updateProjectInLists(tempId, response.data);
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('projects');
      } catch (error: any) {
        useProjectStoreBase.setState((state: any) => ({
          items: state.items.filter((i: any) => i.id !== tempId),
        }));
        toast.error(error.response?.status === 403
          ? t('pmProjects.limitReached', { ns: 'pages' })
          : error.response?.status === 409
          ? t('pmProjects.fromQuote.alreadyConverted', { ns: 'pages' })
          : error.response?.data?.error || error.message || t('common.messages.operationFailed'));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmProjects.editTitle', { ns: 'pages' }) || 'Edit Project' : t('pmProjects.addTitle', { ns: 'pages' }) || 'Add Project'} onClose={handleCloseWithWarning} closeLabel={t('common.actions.close')} />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {!isEditMode && quoteOptions.length > 1 && (
              <FormFieldWithError label={t('pmProjects.fromQuote.label', { ns: 'pages' })} htmlFor="crmQuoteId">
                <CustomSelect
                  id="crmQuoteId"
                  value={watchedQuoteId || ''}
                  onChange={(value) => applyQuote(quotes.find(q => q.id === value))}
                  options={quoteOptions}
                  searchable={quoteOptions.length > 8}
                  searchPlaceholder={t('pmProjects.fromQuote.search', { ns: 'pages' })}
                />
                <p className="mt-1 text-[13px] text-fg-secondary">{t('pmProjects.fromQuote.hint', { ns: 'pages' })}</p>
              </FormFieldWithError>
            )}

            {/* Project Name */}
            <FormFieldWithError
              label={t('pmProjects.fields.name', { ns: 'pages' }) || 'Project Name'}
              required={true}
              error={errors.name?.message}
              htmlFor="name"
            >
              <Input
                id="name"
                {...register('name', {
                  required: t('pmProjects.validation.nameRequired', { ns: 'pages' }) || 'Project name is required',
                  maxLength: {
                    value: 200,
                    message: t('pmProjects.validation.nameMaxLength', { ns: 'pages' }) || 'Name must not exceed 200 characters',
                  },
                })}
                variant={errors.name ? 'error' : 'default'}
                placeholder={t('pmProjects.placeholders.name', { ns: 'pages' }) || 'Enter project name'}
              />
            </FormFieldWithError>

            {/* Description */}
            <FormFieldWithError
              label={t('pmProjects.fields.description', { ns: 'pages' }) || 'Description'}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                {...register('description', {
                  maxLength: {
                    value: 2000,
                    message: t('pmProjects.validation.descriptionMaxLength', { ns: 'pages' }) || 'Description must not exceed 2000 characters',
                  },
                })}
                variant={errors.description ? 'error' : 'default'}
                placeholder={t('pmProjects.placeholders.description', { ns: 'pages' }) || 'Enter project description'}
                rows={3}
              />
            </FormFieldWithError>

            {/* 總預算、專案類型、客戶編號 Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormFieldWithError
                label={t('pmProjects.fields.totalBudget', { ns: 'pages' }) || 'Total Budget'}
                error={errors.totalBudget?.message}
                htmlFor="totalBudget"
              >
                <Input
                  id="totalBudget"
                  type="number"
                  step="1"
                  min="0"
                  {...register('totalBudget', {
                    min: {
                      value: 0,
                      message: t('pmProjects.validation.budgetMin', { ns: 'pages' }) || 'Budget must be positive',
                    },
                  })}
                  variant={errors.totalBudget ? 'error' : 'default'}
                  placeholder="0"
                  disabled={hasPackages || !!selectedQuote}
                  className={`[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${hasPackages || selectedQuote ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`}
                />
                {hasPackages && (
                  <p className="mt-1 text-[13px] text-fg-secondary">
                    {t('pmProjects.budgetFromPackages', { ns: 'pages' }) || 'Sum of packages; edit in the Packages tab'}
                  </p>
                )}
              </FormFieldWithError>

              <div>
                <Label htmlFor="projectType">
                  {t('pmProjects.fields.projectType', { ns: 'pages' }) || 'Project Type'}
                  <span className="text-fg ml-1">*</span>
                </Label>
                <CustomSelect
                  id="projectType"
                  {...register('projectType', {
                    required: t('pmProjects.validation.projectTypeRequired', { ns: 'pages' }) || 'Project type is required',
                  })}
                  value={watchedProjectType}
                  onChange={(value) => setValue('projectType', value, { shouldDirty: true })}
                  options={getProjectTypeOptions(t)}
                />
                {errors.projectType && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.projectType.message}</p>
                )}
              </div>

              <FormFieldWithError
                label={t('pmProjects.fields.customer', { ns: 'pages' })}
                htmlFor="customerId"
              >
                <CustomSelect
                  id="customerId"
                  value={watchedCustomerId || ''}
                  onChange={(value) => setValue('customerId', value, { shouldDirty: true })}
                  options={customerOptions}
                  disabled={!!selectedQuote}
                  searchable
                  searchPlaceholder={t('pmProjects.searchCustomer', { ns: 'pages' })}
                />
              </FormFieldWithError>
            </div>

            {/* Consulting Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmProjects.fields.consultingStartDate', { ns: 'pages' }) || 'Consulting Start Date'}
                error={errors.consultingStartDate?.message}
                htmlFor="consultingStartDate"
              >
                <Input
                  id="consultingStartDate"
                  type="date"
                  {...register('consultingStartDate')}
                  variant={errors.consultingStartDate ? 'error' : 'default'}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmProjects.fields.consultingEndDate', { ns: 'pages' }) || 'Consulting End Date'}
                error={errors.consultingEndDate?.message}
                htmlFor="consultingEndDate"
              >
                <Input
                  id="consultingEndDate"
                  type="date"
                  {...register('consultingEndDate')}
                  variant={errors.consultingEndDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            {/* Delivery Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmProjects.fields.expectedDeliveryDate', { ns: 'pages' }) || 'Expected Delivery Date'}
                error={errors.expectedDeliveryDate?.message}
                htmlFor="expectedDeliveryDate"
              >
                <Input
                  id="expectedDeliveryDate"
                  type="date"
                  {...register('expectedDeliveryDate')}
                  variant={errors.expectedDeliveryDate ? 'error' : 'default'}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmProjects.fields.actualDeliveryDate', { ns: 'pages' }) || 'Actual Delivery Date'}
                error={errors.actualDeliveryDate?.message}
                htmlFor="actualDeliveryDate"
              >
                <Input
                  id="actualDeliveryDate"
                  type="date"
                  {...register('actualDeliveryDate')}
                  variant={errors.actualDeliveryDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className={dialogButtonClass.primary}
            >
              {isEditMode
                ? t('common.actions.save') || 'Save'
                : t('common.actions.create') || 'Create'}
            </button>
          </DialogFooter>
        </form>
    </DialogShell>

      {/* 關閉確認對話框 */}
      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default ProjectDialog;
